import { Link } from "react-router-dom";
import { Plane, Building2, Car, Ticket, ArrowRight, Mail, Sparkles } from "lucide-react";

const TYPE_ICONS = { flight: Plane, hotel: Building2, car: Car, event: Ticket };

const NOISE_BG =
  "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

function daysUntil(dateStr, today) {
  const ms = new Date(dateStr + "T00:00:00") - new Date(today + "T00:00:00");
  return Math.round(ms / 86400000);
}

function fmtTime(iso) {
  if (!iso) return "";
  const m = iso.match(/T(\d{2}:\d{2})/);
  return m ? m[1] : "";
}

function StatusBadge({ status }) {
  const planned = status === "planned";
  const cancelled = status === "cancelled";
  return (
    <span
      className={`font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 border ${
        cancelled
          ? "bg-red-50 text-error border-red-200 line-through"
          : planned
          ? "bg-[#F3E7D3] text-[#B07B2E] border-[#E9D5AE]"
          : "bg-[#DFE9DC] text-success border-[#C9DCC7]"
      }`}
    >
      {cancelled ? "Cancelled" : planned ? "Planned" : "Confirmed"}
    </span>
  );
}

export default function NextTripHero({ trip, reservations, today }) {
  if (!trip) {
    return (
      <div className="relative rounded-card border border-hairline bg-surface h-full p-8 md:p-10 overflow-hidden" data-testid="next-trip-hero-empty">
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: NOISE_BG }} />
        <p className="label-tag mb-4">Next departure</p>
        <h2 className="heading-h1 text-4xl md:text-5xl lg:text-6xl text-sand-300 mb-6">
          No departures<br />scheduled.
        </h2>
        <div className="flex flex-wrap gap-3">
          <Link to="/manual" className="btn-swiss btn-primary text-sm" data-testid="hero-paste-btn">
            <Mail size={16} className="mr-2" /> Paste an email
          </Link>
          <Link to="/plan/ai" className="btn-swiss btn-outline text-sm" data-testid="hero-plan-btn">
            <Sparkles size={16} className="mr-2" /> Plan with AI
          </Link>
        </div>
      </div>
    );
  }

  const dTo = trip.start_date ? daysUntil(trip.start_date, today) : null;
  const inProgress = dTo !== null && dTo <= 0;
  const countdown = inProgress ? "IN PROGRESS" : `T\u2212${dTo} ${dTo === 1 ? "DAY" : "DAYS"}`;
  const dates = [trip.start_date, trip.end_date].filter(Boolean).join(" → ");

  return (
    <div className="relative rounded-card border border-hairline bg-surface h-full overflow-hidden flex flex-col" data-testid="next-trip-hero">
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: NOISE_BG }} />
      <div className="p-8 md:p-10 flex-1">
        <div className="flex items-start justify-between gap-4 mb-5">
          <p className="label-tag">Next departure</p>
          <span
            className={`font-mono text-xs font-semibold tracking-[0.15em] px-2.5 py-1 ${
              inProgress ? "bg-success text-white" : "bg-ember text-white"
            }`}
            data-testid="hero-countdown"
          >
            {countdown}
          </span>
        </div>
        <h2 className="heading-h1 text-4xl md:text-5xl lg:text-6xl break-words mb-3" data-testid="hero-trip-name">
          {trip.destination || trip.name}
        </h2>
        {trip.destination && trip.destination !== trip.name && (
          <p className="text-sand-500 text-sm mb-2">{trip.name}</p>
        )}
        {dates && <p className="font-mono text-sm text-sand-600" data-testid="hero-dates">{dates}</p>}
      </div>

      {reservations.length > 0 && (
        <ul className="border-t border-sand-200 divide-y divide-sand-100">
          {reservations.slice(0, 3).map((r) => {
            const Icon = TYPE_ICONS[r.type] || Plane;
            return (
              <li key={r.id} className="px-8 md:px-10 py-3 flex items-center gap-3" data-testid={`hero-res-${r.id}`}>
                <Icon size={14} className="text-sand-400 shrink-0" />
                <span className="text-sm text-sand-800 truncate flex-1">{r.title}</span>
                <span className="font-mono text-xs text-sand-500 hidden sm:block">{fmtTime(r.start)}</span>
                <StatusBadge status={r.booking_status} />
              </li>
            );
          })}
        </ul>
      )}

      <Link
        to={`/trips/${trip.id}`}
        className="border-t border-sand-200 px-8 md:px-10 py-4 flex items-center justify-between text-sm font-semibold hover:bg-sand-50 transition-colors group"
        data-testid="hero-view-trip"
      >
        <span>View trip</span>
        <ArrowRight size={16} className="text-sand-400 group-hover:text-ember group-hover:translate-x-1 transition-all" />
      </Link>
    </div>
  );
}
